import { Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import Loader from '@shared/components/Loader';
import ErrorBoundary from './error-page';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { DayJsRange, ConsumptionTableConfigType, StockView, StockViewKey } from '@shared/entity';
import {
  createStockAllowancesTableAllRecords,
  createStockAllowancesTableByProductByUser,
} from '../features/stock/components/store';
import {
  createColumnsByUser,
  createColumnsByProduct,
  createColumnsAllRecords,
} from '../features/stock/consumption/components/ConsumptionTable/columns';
import ConsumptionFilters from '../features/stock/consumption/components/ConsumptionTable/ConsumptionFilters';
import ConsumptionTable from '../features/stock/consumption/components/ConsumptionTable/ConsumptionTable';
import { useStockAllowancesQuery } from '../features/stock/queries';
import { useAppNavigate, usePersistentFilters } from '@shared/store';
import { useCurrency } from '@features/currency/currency.hooks';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

const useConsumptionData = () => {
  const navigate = useAppNavigate();
  const intl = useIntl();
  const { teamId } = useParams();
  const [filters, updateFilter] = usePersistentFilters();
  const { preferredCurrency } = useCurrency();
  const { trackEvent } = useAnalytics();

  const {
    consumption: { dates, view },
  } = filters;

  const { control } = useForm<Partial<DayJsRange>>({
    defaultValues: {
      from: dayjs(dates.from),
      to: dayjs(dates.to),
    },
  });

  const { data: stockAllowances, isLoading } = useStockAllowancesQuery({
    teamId,
    fromDate: dayjs(dates.from),
    toDate: dayjs(dates.to),
  });

  const tableData = useMemo(() => {
    if (!stockAllowances) {
      return null;
    }

    return {
      allRecords: createStockAllowancesTableAllRecords(stockAllowances),
      byProduct: createStockAllowancesTableByProductByUser(
        stockAllowances,
        stockAllowance => `${stockAllowance.user.name}-${stockAllowance.stockItemName}`,
      ),
      byUser: createStockAllowancesTableByProductByUser(
        stockAllowances,
        stockAllowance => stockAllowance.user.name,
      ),
    };
  }, [stockAllowances]);

  const consumptionTableConfig: ConsumptionTableConfigType | null = useMemo(() => {
    if (!tableData) {
      return null;
    }

    return {
      [StockView.byUser]: {
        rows: tableData.byUser,
        columns: createColumnsByUser(intl),
      },
      [StockView.byProduct]: {
        rows: tableData.byProduct,
        columns: createColumnsByProduct(intl, preferredCurrency),
      },
      [StockView.allRecords]: {
        rows: tableData.allRecords,
        columns: createColumnsAllRecords(navigate, intl, preferredCurrency),
      },
    };
  }, [tableData, navigate, intl, preferredCurrency]);

  const handleViewChange = (filter: StockViewKey) => {
    trackEvent('consumption_viewed', { filter_type: filter, has_filters: true });
    updateFilter(draft => {
      draft.consumption.view = filter;
    });
  };

  const handleDateFromChange = (date?: dayjs.Dayjs | null) => {
    updateFilter(draft => {
      draft.consumption.dates.from = date?.toISOString();
    });
  };

  const handleDateToChange = (date?: dayjs.Dayjs | null) => {
    updateFilter(draft => {
      draft.consumption.dates.to = date?.toISOString();
    });
  };

  return {
    view,
    control,
    isLoading,
    consumptionTableConfig,
    handleViewChange,
    handleDateFromChange,
    handleDateToChange,
  };
};

const Consumption = () => {
  const {
    view,
    control,
    isLoading,
    consumptionTableConfig,
    handleViewChange,
    handleDateFromChange,
    handleDateToChange,
  } = useConsumptionData();

  if (isLoading || !consumptionTableConfig) {
    return <Loader />;
  }

  if (!consumptionTableConfig) {
    return <ErrorBoundary />;
  }

  return (
    <Stack spacing={4}>
      <ConsumptionFilters
        control={control}
        activeView={view}
        onViewChange={handleViewChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
      />
      <Stack spacing={1} height="100%">
        <ConsumptionTable config={consumptionTableConfig} view={view} />
      </Stack>
    </Stack>
  );
};

export default Consumption;
